import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../stores/gameStore';
import { useUIStore } from '../stores/uiStore';
import { useUserStore } from '../stores/userStore';
import { useTimer } from '../hooks/useTimer';
import { fetchSessionQuestions, submitGameResult } from '../services/questionService';
import { sfxCorrect, sfxWrong } from '../utils/audio';
import type { QuestionForClient } from '@beast-keeper/shared';
import TimerRing from '../components/quiz/TimerRing';
import OptionsList from '../components/quiz/OptionsList';
import ProgressBar from '../components/quiz/ProgressBar';
import DifficultyBadge from '../components/quiz/DifficultyBadge';
import FeedbackDisplay from '../components/quiz/FeedbackDisplay';

function shuffleQuestionOptions(q: QuestionForClient): QuestionForClient {
  const pairs = q.options.map((opt, i) => ({ opt, origIdx: i }));
  for (let i = pairs.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
  }
  return {
    ...q,
    options: pairs.map((p) => p.opt) as [string, string, string, string],
    correctIndex: pairs.findIndex((p) => p.origIdx === q.correctIndex),
  };
}

const FUN_FEEDBACK = {
  correct: {
    fast: ['光速抢答！娃都惊呆了 ⚡', '秒杀！你这手速是换尿布练出来的？', '丝滑！奶瓶都还没倒你就答完了 🍼'],
    normal: ['稳如老狗，配方奶都不会洒 👌', '拿捏了，娃给你竖大拇指 👍', '正确！你离神兽饲养员又近了一步'],
    slow: ['终于答对了…但娃的耐心条已见底 😮‍💨', '对了！不过你这反应速度，娃都快哭了'],
  },
  wrong: {
    easy: ['啊？！这是送分题啊饲养员！！😱', '这道题连隔壁老王都会…🤦'],
    medium: ['哦豁，娃可能会嫌弃你3秒 😒', '翻车了！不过没关系，尿布还得换 🫠'],
    hard: ['好吧，这题确实变态…原谅你了 🤷', '地狱级难度，答不对正常（但还是很气）'],
  },
};

const STREAK_MSGS: Record<number, string> = {
  3: '三连对！娃开始对你刮目相看了 👀',
  5: '五连绝世！！娃已经准备好叫你大神了 🏆',
  7: '七连超凡！！！宝妈准备给你发奖状 📜',
  10: '十连！满昏满昏！你就是育儿百科本体 📖',
};

function getCorrectMsg(remaining: number) {
  const pool = remaining > 10 ? FUN_FEEDBACK.correct.fast :
               remaining > 4 ? FUN_FEEDBACK.correct.normal :
               FUN_FEEDBACK.correct.slow;
  return pool[Math.floor(Math.random() * pool.length)];
}

function getWrongMsg(diff: number) {
  const pool = diff === 1 ? FUN_FEEDBACK.wrong.easy :
               diff === 2 ? FUN_FEEDBACK.wrong.medium :
               FUN_FEEDBACK.wrong.hard;
  return pool[Math.floor(Math.random() * pool.length)];
}

export default function QuizScreen() {
  const navigate = useNavigate();
  const { questions, currentIndex, isActive, answerQuestion, nextQuestion, initGame, isFinished } = useGameStore();
  const showStreakToast = useUIStore((s) => s.showStreakToast);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', type: '' as '' | 'correct' | 'wrong' });
  const [loading, setLoading] = useState(false);
  const [qStartTime, setQStartTime] = useState(Date.now());
  const startRef = useRef<() => void>(() => {});

  const handleExpire = useCallback(() => {
    if (revealed) return;
    setRevealed(true);
    const TIMEOUT_MSGS = [
      '超时了！！娃已经哭到缺氧了 😭💨',
      '时间到！你的神气值归零…从换尿布重新练起吧 🫠',
      '叮！您的育儿知识储备已欠费，请充值 📉',
    ];
    setFeedback({ message: TIMEOUT_MSGS[Math.floor(Math.random() * TIMEOUT_MSGS.length)], type: 'wrong' });
    sfxWrong();
    answerQuestion(-1, 20000);
    setTimeout(() => {
      setSelectedIndex(null);
      setRevealed(false);
      setFeedback({ message: '', type: '' });
      setQStartTime(Date.now());
      nextQuestion();
      startRef.current();
    }, 1600);

    // Return the score addition (0 for timeout)
    return 0;
  }, [revealed, answerQuestion, nextQuestion]);

  const { timeLeft, start, stop } = useTimer(20, handleExpire);
  startRef.current = start;

  // Load questions on mount
  useState(() => {
    if (questions.length === 0 || isFinished) {
      setLoading(true);
      fetchSessionQuestions()
        .then((qs) => {
          initGame(qs.map(shuffleQuestionOptions));
          setQStartTime(Date.now());
          start();
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          navigate('/');
        });
    } else {
      start();
    }
  });

  // Navigate to result when game finishes
  if (isFinished) {
    navigate('/result');
    return null;
  }

  const handleSelect = (idx: number) => {
    if (revealed) return;
    setSelectedIndex(idx);
    setRevealed(true);
    stop();

    const timeMs = Date.now() - qStartTime;
    const q = questions[currentIndex];
    const { correct, score, newStreak } = answerQuestion(idx, timeMs);

    if (correct) {
      setFeedback({ message: `+${score}分 · ${getCorrectMsg(timeLeft)}`, type: 'correct' });
      sfxCorrect();
      // Streak toast
      for (const k of [10, 7, 5, 3]) {
        if (newStreak === k && STREAK_MSGS[k]) {
          showStreakToast(STREAK_MSGS[k]);
          break;
        }
      }
    } else {
      setFeedback({ message: getWrongMsg(q.difficulty), type: 'wrong' });
      sfxWrong();
    }

    setTimeout(() => {
      setSelectedIndex(null);
      setRevealed(false);
      setFeedback({ message: '', type: '' });
      setQStartTime(Date.now());
      nextQuestion();
      start();
    }, 1400);
  };

  if (loading || questions.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <p style={{ color: 'var(--color-text-secondary)' }}>加载题目中...🍼</p>
      </div>
    );
  }

  const q = questions[currentIndex];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: 'var(--space-lg) var(--space-md)' }}>
      {/* Header: difficulty + progress + counter */}
      <div className="quiz-header">
        <DifficultyBadge difficulty={q.difficulty} />
        <ProgressBar total={questions.length} current={currentIndex} />
        <div className="q-counter">{currentIndex + 1}/{questions.length}</div>
      </div>

      {/* Timer */}
      <TimerRing timeLeft={timeLeft} duration={20} />

      {/* Question */}
      <div className="question-area">
        <div className="question-text">{q.text}</div>
      </div>

      {/* Options */}
      <OptionsList
        options={q.options}
        selectedIndex={selectedIndex}
        correctIndex={q.correctIndex}
        disabled={revealed}
        revealed={revealed}
        onSelect={handleSelect}
      />

      {/* Feedback */}
      <FeedbackDisplay message={feedback.message} type={feedback.type} />
    </div>
  );
}
